<template>
    <v-card outlined class="card-format">
        <v-card-title class="mb-2 custom-title d-flex align-center justify-space-between">
            <span>REVIEW QUOTE</span>
            <v-btn color="green" dark large @click="update()" v-if="edit_quote">
                <v-icon left>mdi-check</v-icon>UPDATE QUOTE
            </v-btn>
            <v-btn color="green" dark large @click="finish()" v-else>
                <v-icon left>mdi-check</v-icon>SAVE QUOTE
            </v-btn>
        </v-card-title>
        <v-card-text>
            <div v-for="(builds, index) in all_builds[0]" :key="index">
                <v-row v-if="builds && builds.supplier">
                    <v-col cols="4" class="review-quote-left-text">
                        <b style="color:#101010;">ACCOMMODATION</b>
                        <div v-if="builds.start_date && builds.end_date">
                            {{
                                $date(builds.start_date).format('ddd, Do MMMM')
                            }}
                            —
                            {{
                                $date(builds.end_date).format('ddd, Do MMMM YYYY')
                            }}
                        </div>
                    </v-col>
                    <v-col cols="6" class="review-quote-right-text">
                        <label style="color:#101010;">{{ builds.supplier.supplier_name }}</label><br>
                        {{ $date(builds.end_date).diff(builds.start_date, 'day') }} night(s)
                    </v-col>
                    <v-col cols="2" class="review-quote-right-text text-right">
                        <div v-if="builds.options">
                            {{ get_selected_company.currency }}{{ builds.options[0].customer_price *
                                $date(builds.end_date).diff(builds.start_date, 'day') }}
                        </div>
                    </v-col>
                </v-row>
                <hr class="custom-hr" v-if="builds && builds.supplier">
            </div>

            <div v-for="(entry, index) in sortedActivityBuilds" :key="'activity' + index">
                <v-row>
                    <v-col cols="4" class="review-quote-left-text">
                        <b style="color:#101010;">{{ entry.quote }}</b>
                        <div v-if="entry.builds.start_date">
                            {{
                                $date(entry.builds.start_date).format('ddd, Do MMMM YYYY')
                            }}
                        </div>
                    </v-col>
                    <v-col cols="8">
                        <v-row v-for="(option, option_index) in entry.builds.options" :key="option_index">
                            <v-col cols="8" class="review-quote-right-text">
                                <label style="color:#101010;">
                                    {{ entry.builds.supplier.supplier_name + ' — ' + option.name }}
                                </label>
                            </v-col>
                            <v-col cols="4" class="review-quote-right-text text-right">
                                <div v-if="option">
                                    {{ get_selected_company.currency }}{{ perPersonCustomerPrice(option, groupsize) }}
                                    <div v-if="isPerGroup(option)" class="per-group-note"
                                        :class="{ 'per-group-note--missing': !(groupsize > 0) }">
                                        {{ get_selected_company.currency }}{{ option.customer_price }} per group{{ groupsize > 0 ? ' ÷ ' + groupsize : ' – enter a group size' }}
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <hr class="custom-hr" v-if="showDivider(index)">
            </div>

            <v-row class="mt-10 mb-5">
                <v-col cols="12" class="text-right">
                    <div class="d-flex" style="justify-content:end;">
                        <div class="quote-total mr-15">SUBTOTAL:</div>
                        <div class="accom-price">{{ get_selected_company.currency }}{{ details.subtotal = getSubTotal()
                            }}</div>
                    </div>
                </v-col>
            </v-row>

            <v-row class="mt-10 mb-5 quote-total">
                <v-col cols="4"></v-col>
                <v-col cols="8">
                    <div class="mb-2">Discount</div>
                    <v-row>
                        <v-col cols="6">
                            <v-select v-model="details.discount_type" label="Type" :items="['amount', 'percent']" filled
                                outlined dense>
                                <template v-slot:selection="{ item }">
                                    <b>{{ item }}</b>
                                </template>
                                <template v-slot:item="{ item }">
                                    <b>{{ item }}</b>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field v-model="details.discount" type="number" min="0"
                                :max="details.discount_type === 'percent' ? 100 : null" label="Amount" filled
                                outlined dense></v-text-field>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="4"></v-col>
                <v-col cols="8">
                    <div class="mb-2">Surcharge</div>
                    <v-row>
                        <v-col cols="6">
                            <v-select v-model="details.surcharge_type" label="Type" :items="['amount', 'percent']"
                                filled outlined dense>
                                <template v-slot:selection="{ item }">
                                    <b>{{ item }}</b>
                                </template>
                                <template v-slot:item="{ item }">
                                    <b>{{ item }}</b>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field v-model="details.surcharge" type="number" min="0"
                                :max="details.surcharge_type === 'percent' ? 100 : null" label="Amount" filled
                                outlined dense></v-text-field>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="4"></v-col>
                <v-col cols="4">
                    Profit Margin for {{ quote_location ? quote_location.name : '' }} {{ lead.event_type.name }}:
                </v-col>
                <v-col cols="4" v-if="selected_profit_margin[0]">
                    <div v-if="all_builds[0]">
                        {{ get_selected_company.currency }}{{ selected_profit_margin[0].accommodation_margin }}
                    </div>
                    <div v-else>
                        {{ get_selected_company.currency }}{{ selected_profit_margin[0].activity_only_margin }}
                    </div>
                </v-col>
                <v-col cols="4" v-else-if="profit_margins_loaded">
                    <div class="no-margin-warning">No margin configured — excluded from total</div>
                </v-col>
            </v-row>

            <!-- Low-profit warning: the total leaves less profit over the suppliers' net prices than
                 the location's margin (or the company minimum). See docs/reference/LOW_PROFIT_QUOTES.md. -->
            <v-alert v-if="low_profit" type="error" outlined dense icon="mdi-alert" class="mt-6 mb-0 low-profit-alert">
                Low profit: this quote makes {{ get_selected_company.currency }}{{ low_profit.profit.toFixed(2) }} per
                person, below the {{ get_selected_company.currency }}{{ low_profit.minimum.toFixed(2) }} minimum
                <template v-if="low_profit.source === 'location'">for {{ quote_location ? quote_location.name : '' }}
                    {{ lead.event_type.name }}</template><template v-else>for the company</template>. Please check the
                discount and prices before sending it.
            </v-alert>

            <v-row class="mt-10 mb-5">
                <v-col cols="12" class="text-right">
                    <div class="d-flex" style="justify-content:end;">
                        <div class="quote-total mr-15">TOTAL:</div>
                        <div class="accom-price">{{ get_selected_company.currency }}{{ details.total = getTotal() }}
                        </div>
                    </div>
                </v-col>
                <v-col cols="12">
                    <div class="per-person">PER PERSON</div>
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-actions class="custom-title d-flex align-center justify-space-between">
            <v-btn color="#F7F7F7" @click="back()">BACK</v-btn>
            <v-btn color="green" dark large @click="update()" v-if="edit_quote"
                style="padding:0 19.5555555556px;">
                <v-icon left>mdi-check</v-icon>UPDATE QUOTE
            </v-btn>
            <v-btn color="green" dark large @click="finish()" v-else
                style="padding:0 19.5555555556px;">
                <v-icon left>mdi-check</v-icon>SAVE QUOTE
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import { perPersonSupplierCost, perPersonCustomerPrice, isPerGroup } from '@/utils/finalRates'
export default {
    components: {
    },
    props: [
        'all_builds', 'edit_quote', 'lead', 'quote_location', 'groupsize'
    ],
    data: () => ({
        details: {
            type: 'tailormade',
            subtotal: 0,
            discount_type: 'amount',
            discount: 0,
            surcharge_type: 'amount',
            surcharge: 0,
            total: 0,
        },
        quotes: [
            'DAY ACTIVITIES', 'NIGHT ACTIVITIES', 'BREAKFAST', 'FINGERFOOD', 'DINNER', 'TRANSPORT', 'ADDONS'
        ],
        classes: {
        },
        selected_profit_margin: [],
        profit_margins_loaded: false,
    }),
    async mounted() {
        this.getProfitMargins()
    },
    created() {
    },
    watch: {
        // edit_quote can still be null when this component first mounts (it arrives
        // asynchronously from the parent's quote load), so a one-time mounted() hook
        // can miss it. Watching keeps discount/surcharge/type in sync once it lands,
        // without re-mounting Review and losing in-progress edits.
        edit_quote: {
            handler(newVal) {
                if (newVal) this.getEditData()
            },
            immediate: true,
        },
    },
    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        }),
        // Flattens every non-accommodation bucket into one list, tagged with its category
        // label, and sorts by date first so e.g. a Friday transfer shows before a Saturday
        // night activity instead of being grouped after it by type.
        sortedActivityBuilds() {
            const flattened = []
            this.quotes.forEach((quote, quote_index) => {
                const builds = this.all_builds[quote_index + 1] || []
                builds.forEach((builds_entry) => {
                    if (builds_entry && builds_entry.supplier && builds_entry.options && builds_entry.options.length > 0) {
                        flattened.push({ quote, quote_index, builds: builds_entry })
                    }
                })
            })
            return flattened.sort((a, b) => {
                const a_date = a.builds.start_date || ''
                const b_date = b.builds.start_date || ''
                if (a_date !== b_date) {
                    return a_date < b_date ? -1 : 1
                }
                return a.quote_index - b.quote_index
            })
        },
        // { profit, minimum, source } per person when the total leaves less than the minimum over
        // the suppliers' net prices, else null. Minimum: the location's margin, else the company's
        // Minimum Quote Profit; neither set means no check. Same rule as the API's LowProfitService.
        low_profit() {
            const margin = this.selected_profit_margin[0] ? this.appliedMargin() : null
            const fallback = this.get_selected_company.minimum_quote_profit
            const has_margin = margin !== null && margin !== undefined && margin !== ''
            const minimum = parseFloat(has_margin ? margin : fallback)
            if (isNaN(minimum)) return null
            const profit = parseFloat(this.getTotal()) - this.getCost()
            const source = has_margin ? 'location' : 'company'
            return Math.round(profit * 100) < Math.round(minimum * 100) ? { profit, minimum, source } : null
        },
    },
    methods: {
        perPersonCustomerPrice,
        isPerGroup,
        getEditData() {
            if (this.edit_quote) {
                this.details.type = this.edit_quote.type
                this.details.subtotal = this.edit_quote.subtotal
                this.details.discount_type = this.edit_quote.discount_type
                this.details.discount = this.edit_quote.discount
                this.details.surcharge_type = this.edit_quote.surcharge_type
                this.details.surcharge = this.edit_quote.surcharge
                this.details.total = this.edit_quote.total
            }
        },
        async getProfitMargins() {
            const response = await this.$axios.get(`admin/profit_margins/get_profit_margins/${this.get_selected_company.id}`)
            const result = response.data
            if (result.response == true) {
                const quote_location_id = this.quote_location ? this.quote_location.id : null
                this.selected_profit_margin = result.data.filter((item) => {
                    return item.event_type_id == this.lead.event_type_id && item.location_id == quote_location_id
                })
            }
            this.profit_margins_loaded = true
        },
        // No divider between entries that share the same day, regardless of type, so
        // same-day services read as one visual block. Always shown after the last entry
        // to separate the list from the subtotal below.
        showDivider(index) {
            if (index === this.sortedActivityBuilds.length - 1) return true
            return this.sortedActivityBuilds[index].builds.start_date !== this.sortedActivityBuilds[index + 1].builds.start_date
        },
        back() {
            this.$emit('back')
        },
        finish() {
            this.$emit('finish', this.details)
        },
        update() {
            this.$emit('update', this.details)
        },
        getSubTotal() {
            if (this.all_builds) {
                let subtotal = 0
                // Bucket 0 is always the dedicated Accommodation step (populated by
                // Accommodation.vue) - price it per night. Every other bucket (1-7) is
                // never accommodation, even if the same multi-type supplier also offers
                // Accommodation elsewhere, so it's priced flat regardless of the
                // supplier's own type.
                this.all_builds.forEach((builds, bucket_index) => {
                    builds.forEach(build => {
                        let rate = (build && build.options) ? build.options.reduce((a, b) => a + perPersonCustomerPrice(b, this.groupsize), 0) : 0
                        if (bucket_index === 0 && build.start_date && build.end_date) {
                            const nights = this.$date(build.end_date).diff(build.start_date, 'day')
                            rate *= nights > 0 ? nights : 1
                        }
                        subtotal += rate
                    })
                })
                return subtotal.toFixed(2)
            }
            else {
                return 0
            }
        },
        // Supplier cost per person for the low-profit check: each option's net Supplier Price, or its
        // customer price when no net price is entered. A per-group price is divided by the quote's
        // group size. Accommodation per night, like getSubTotal().
        getCost() {
            let cost = 0
            ;(this.all_builds || []).forEach((builds, bucket_index) => {
                builds.forEach(build => {
                    let rate = (build && build.options) ? build.options.reduce((sum, option) => {
                        return sum + perPersonSupplierCost(option, this.groupsize)
                    }, 0) : 0
                    if (bucket_index === 0 && build.start_date && build.end_date) {
                        const nights = this.$date(build.end_date).diff(build.start_date, 'day')
                        rate *= nights > 0 ? nights : 1
                    }
                    cost += rate
                })
            })
            return cost
        },
        // The margin added to the total (and the low-profit minimum): accommodation or activity only.
        appliedMargin() {
            const margin = this.selected_profit_margin[0]
            if (!margin) return 0
            return this.all_builds[0] ? margin.accommodation_margin : margin.activity_only_margin
        },
        getTotal() {
            let discount = 0
            let surcharge = 0
            let margin = 0
            if (this.details.discount_type == 'amount' && this.details.discount != '' && this.details.discount != null) {
                discount = this.details.discount
            }
            else if (this.details.discount_type == 'percent' && this.details.discount != '' && this.details.discount != null) {
                discount = (this.details.discount / 100) * this.details.subtotal
            }
            if (this.details.surcharge_type == 'amount' && this.details.surcharge != '' && this.details.surcharge != null) {
                surcharge = this.details.surcharge
            }
            else if (this.details.surcharge_type == 'percent' && this.details.surcharge != '' && this.details.surcharge != null) {
                surcharge = (this.details.surcharge / 100) * this.details.subtotal
            }

            if (this.selected_profit_margin[0]) {
                margin = this.appliedMargin()
            }

            let grand_total = this.getSubTotal() - parseFloat(discount) + parseFloat(surcharge) + parseFloat(margin)
            return Math.max(0, grand_total).toFixed(2)
        },
    }
}
</script>

<style lang="scss" scoped>
.per-group-note {
    font-size: 12px;
    color: #6B7280;
}
.per-group-note--missing {
    color: #C62828;
}
.card-format {
    padding: 24px;
    border-radius: 20px !important;

    .card-footer-format {
        border-top: 1px solid #E9E9E9;
        background-color: #E9E9E9;
    }
}

.card-footer-format {
    .pagination-font-format {
        font-family: 'Work Sans';
        font-size: 14px;
    }

    .bg-color-white {
        background-color: white;
    }
}

:deep(.v-input__slot) {
    background: white !important;
}

:deep(.v-stepper__header) {
    box-shadow: none !important;
}

.accom-price {
    color: #FAAF3A;
    font-size: 36px;
    font-weight: 600;
}

.custom-title {
    font-family: 'Manrope';
    font-size: 14px !important;
    color: #3988AA;
    font-weight: 600;
}

.sublabel {
    color: #7C7C7C;
    font-family: Manrope;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
}

.description {
    color: #7D7D7D;
    font-family: Manrope;
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.summary {
    color: #7C7C7C;
    font-family: Manrope;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    margin-bottom: 20px;
}

.review-quote-left-text {
    font-family: Manrope;
    font-size: 14px;
    font-weight: 700;
    line-height: 19px;
    letter-spacing: 0em;
    text-align: left;
    color: #7C7C7C;
}

:deep(.custom-buttons span) {
    font-family: Manrope;
    font-size: 13px !important;
    font-style: normal;
    font-weight: 600 !important;
    line-height: normal;
    box-shadow: none !important;
    width: 58px;
}

:deep(.custom-buttons button) {
    padding: 8px 7px 8px 7px !important;
    border-radius: 5px;
}

.review-quote-right-text {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;
}

.custom-hr {
    border: 0.5px solid #D3D3D3;
    height: 0.5px;
    margin-top: 10px;
    margin-bottom: 10px;
}

.quote-total {
    font-family: Manrope;
    font-size: 14px;
    font-weight: 700;
    line-height: 19px;
    letter-spacing: 0em;
    color: #101010;
}

.low-profit-alert {
    font-family: Manrope;
    font-size: 14px;
    font-weight: 600;
}

.no-margin-warning {
    font-family: Manrope;
    font-size: 12px;
    font-weight: 600;
    color: #D32F2F;
}

.per-person {
    font-family: Manrope;
    font-size: 14px;
    font-weight: 700;
    line-height: 19px;
    letter-spacing: 0em;
    text-align: right;
    color: #7C7C7C;
}
</style>